import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import type { DatumPlaneHistorySnapshot } from '../../reference';
import { DATUM_PLANE_HISTORY_ENTITY_ID } from '../../reference';
import { isSerializablePlainData } from './SerializablePlainData';

//------ 基准面撤销重做 开始------
/**
 * 基准面历史 adapter ID。
 */
export const DATUM_PLANE_HISTORY_ADAPTER_ID = 'history-domain:datum-plane';

/**
 * 基准面历史运行时存储。
 */
export interface DatumPlaneHistoryStore {
    /**
     * 恢复基准面历史快照。
     *
     * @param snapshot 基准面历史快照。
     */
    readonly restoreDatumPlaneHistorySnapshot: (snapshot: DatumPlaneHistorySnapshot) => void;
}

/**
 * 判断对象是否为普通对象。
 *
 * @param value 待判断对象。
 * @returns 是否为普通对象。
 */
function isPlainObject(value: object): boolean {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}

/**
 * 递归移除历史快照中的 `undefined` 字段。
 *
 * @param value 待归一化值。
 * @returns 已移除 `undefined` 的值。
 */
function omitUndefinedDeep(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value
            .map((item) => omitUndefinedDeep(item))
            .filter((item) => item !== undefined);
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    if (!isPlainObject(value)) {
        return value;
    }

    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
            .map(([key, item]) => [key, omitUndefinedDeep(item)] as const)
            .filter(([, item]) => item !== undefined),
    );
}

/**
 * 归一化基准面历史快照。
 *
 * @param snapshot 来源快照。
 * @returns 可序列化快照。
 */
function normalizeDatumPlaneHistorySnapshot(snapshot: DatumPlaneHistorySnapshot): DatumPlaneHistorySnapshot {
    return omitUndefinedDeep(snapshot) as DatumPlaneHistorySnapshot;
}

/**
 * 归一化基准面实体快照。
 *
 * @param snapshot 来源实体快照。
 * @returns 可序列化实体快照。
 */
function normalizeDatumPlaneEntitySnapshot(
    snapshot: HistoryEntitySnapshot<DatumPlaneHistorySnapshot>,
): HistoryEntitySnapshot<DatumPlaneHistorySnapshot> {
    if (!snapshot.exists || !snapshot.data) {
        return snapshot;
    }

    return {
        ...snapshot,
        data: normalizeDatumPlaneHistorySnapshot(snapshot.data),
    };
}

/**
 * 判断基准面快照是否为 plain data。
 *
 * @param snapshot 基准面快照。
 * @returns 是否为可序列化 plain data。
 */
export function isPlainDatumPlaneHistorySnapshot(snapshot: DatumPlaneHistorySnapshot): boolean {
    const normalizedSnapshot = normalizeDatumPlaneHistorySnapshot(snapshot);

    return typeof normalizedSnapshot.managerId === 'string'
        && Array.isArray(normalizedSnapshot.planes)
        && isSerializablePlainData(normalizedSnapshot);
}

/**
 * 应用基准面历史快照。
 *
 * @param input 补丁应用输入。
 */
function applyDatumPlaneSnapshot(
    input: HistoryPatchApplyInput<DatumPlaneHistorySnapshot, DatumPlaneHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.restoreDatumPlaneHistorySnapshot({
            managerId: DATUM_PLANE_HISTORY_ENTITY_ID,
            planes: [],
        });
        return;
    }

    if (!snapshot.data) {
        throw new Error(`基准面 ${input.patch.entityId} 缺少快照数据。`);
    }

    const normalizedSnapshot = normalizeDatumPlaneHistorySnapshot(snapshot.data);
    if (!isPlainDatumPlaneHistorySnapshot(normalizedSnapshot)) {
        throw new Error(`基准面 ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.restoreDatumPlaneHistorySnapshot(normalizedSnapshot);
}

/**
 * 比较基准面历史快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areDatumPlaneSnapshotsEqual(
    left: HistoryEntitySnapshot<DatumPlaneHistorySnapshot>,
    right: HistoryEntitySnapshot<DatumPlaneHistorySnapshot>,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建基准面历史 adapter。
 *
 * @returns 基准面历史 adapter。
 */
export function createDatumPlaneHistoryAdapter(): HistoryDomainAdapter<
    DatumPlaneHistorySnapshot,
    DatumPlaneHistoryStore
> {
    return {
        adapterId: DATUM_PLANE_HISTORY_ADAPTER_ID,
        kind: 'datum',
        applySnapshot: applyDatumPlaneSnapshot,
        areSnapshotsEqual: areDatumPlaneSnapshotsEqual as HistoryDomainAdapter<
            DatumPlaneHistorySnapshot,
            DatumPlaneHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建基准面历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 基准面历史补丁。
 */
export function createDatumPlaneHistoryPatch(
    patch: Omit<EntityPatch<DatumPlaneHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<DatumPlaneHistorySnapshot> {
    return {
        ...patch,
        before: normalizeDatumPlaneEntitySnapshot(patch.before),
        after: normalizeDatumPlaneEntitySnapshot(patch.after),
        entityKind: 'datum',
        adapterId: DATUM_PLANE_HISTORY_ADAPTER_ID,
    };
}
//------ 基准面撤销重做 结束------
